import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/styles';
import {
  Radio,
  TextField,
  Button,
  Tooltip,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  IconButton,
  RadioGroup,
  FormControlLabel,
  FormControl
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ClearIcon from '@material-ui/icons/Clear';

const useStyles = makeStyles(theme => ({
  button: {
    margin: '0px 8px',
    fontWeight: 'normal',
    color: 'rgba(0,0,0,.55) !important',
    opacity: '1 !important'
  },
  selected: {
    color: theme.palette.primary.main
  },
  listItem: {
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    width: '100%'
  },
  label: {
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    width: 'auto'
  },
  clearButton: {
    '&:hover': {
        color: theme.palette.primary.main + '!important'
    }
  },
  searchBar: {
    paddingRight: '0px'
  },
  searchInput: {
    '& .MuiFormLabel-root' : {
      marginTop: '4px'
    }
  },
  autocompleteListbox: {
    '& li[aria-disabled="true"]': {
      pointerEvents: 'inherit !important',
    },
    '& li[aria-disabled="true"]:hover, & li[aria-disabled="true"]:focus': {
      background: 'white !important',
    },
    '& li[aria-disabled="true"]:active': {
      background: 'white !important',
      pointerEvents: 'none !important',
    }
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start'
  },
  radioLabel: {
    margin: '0 4px',
    '& .MuiFormControlLabel-label': {
      fontSize: '0.875rem',
      color: 'rgba(0,0,0,.65)'
    },
    '& .MuiRadio-root': {
      paddingLeft: '4px',
      paddingRight: '4px'
    }
  }
}));

export default function AutoCompleteRadioFilter(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { filter, action, className, showStatusFilter, statusFilter, onStatusChange, onSearch } = props;
  const { options, onChange, title, returnSelectedObject, isOpen, error } = filter;
  const [selectedOption, setSelectedOption] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [internalStatusFilter, setInternalStatusFilter] = React.useState('all');
  const [inputValue, setInputValue] = React.useState('');
  const anchorRef = React.useRef(null);
  const debounceRef = React.useRef(null);
  const currentStatusFilter = statusFilter !== undefined ? statusFilter : internalStatusFilter;

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setOpen(isOpen);
    }
  }, [isOpen]);

  useEffect(() => {
    const checkedOption = options.find(i => i.checked);
    setSelectedOption(checkedOption);
    // Update input value to show selected campaign name
    if (checkedOption) {
      setInputValue(checkedOption.displayName || checkedOption.value || '');
    }
  }, [options]);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleStatusChange = (event) => {
    const newValue = event.target.value;
    if (onStatusChange) {
      onStatusChange(newValue);
    } else {
      setInternalStatusFilter(newValue);
    }
  };

  const handleChange = option => {
    setSelectedOption(option);
    onChange(returnSelectedObject ? option : option.value);
    if (action) {
      dispatch(action);
    }
  };

  const handleClear = () => {
    setSelectedOption(null);
    setInputValue('');
    onChange(returnSelectedObject ? { value: '', displayName: '' } : '');
    if (onSearch) {
      onSearch('');
    }
    if (action) {
      dispatch(action);
    }
  };

  return (
    <>
      <Button
        aria-label={`Filter ${title}`}
        ref={anchorRef}
        className={`${className ? className : ''} ${selectedOption ? classes.selected : classes.button}`}
        onClick={handleToggle}
        endIcon={<ExpandMoreIcon />}>
        {title}
      </Button>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition>
        {({ TransitionProps, placement }) => (
          <Paper style={{ marginLeft: showStatusFilter ? '8px' : '0px' }}>
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom' ? 'center top' : 'center bottom'
              }}>
              <ClickAwayListener onClickAway={handleClose}>
                <div id="split-button-menu">
                <Autocomplete
                    id="radio-tags"
                    disableCloseOnSelect
                    options={options}
                    open={true}
                    role={undefined}
                    renderTags={() => {}}
                    size="small"
                    inputValue={inputValue}
                    onInputChange={(event, newInputValue, reason) => {
                      // Only update input value when user types, not on reset
                      if (reason === 'input' || reason === 'clear') {
                        setInputValue(newInputValue);
                      }
                      if (onSearch && reason === 'input') {
                        // Debounce the search
                        if (debounceRef.current) {
                          clearTimeout(debounceRef.current);
                        }
                        debounceRef.current = setTimeout(() => {
                          onSearch(newInputValue);
                        }, 300);
                      }
                    }}
                    filterOptions={onSearch ? (x) => x : undefined}
                    getOptionLabel={option =>
                        option.displayName ? option.displayName : option.value
                    }
                    getOptionDisabled={option => option.disabled}
                    PaperComponent={({ children }) => (
                      <Paper>
                        {showStatusFilter && (
                          <FormControl component="fieldset" style={{ width: '100%' }}>
                            <RadioGroup
                              className={classes.radioGroup}
                              value={currentStatusFilter}
                              onChange={handleStatusChange}
                              row
                              style={{ justifyContent: 'flex-start', flexWrap: 'nowrap' }}
                            >
                              <FormControlLabel
                                value="all"
                                control={<Radio color="primary" size="small" />}
                                label="All"
                                className={classes.radioLabel}
                              />
                              <FormControlLabel
                                value="active"
                                control={<Radio color="primary" size="small" />}
                                label="Active"
                                className={classes.radioLabel}
                              />
                              <FormControlLabel
                                value="archived"
                                control={<Radio color="primary" size="small" />}
                                label="Archived"
                                className={classes.radioLabel}
                              />
                            </RadioGroup>
                          </FormControl>
                        )}
                        {error && options.length === 0 && (
                          <div style={{ padding: '16px', color: '#f44336', fontSize: '0.875rem' }}>
                            {error.message || 'Error loading campaigns'}
                          </div>
                        )}
                        {!error && options.length === 0 && inputValue && (
                          <div style={{ padding: '16px', color: '#666', fontSize: '0.875rem' }}>
                            No results found
                          </div>
                        )}
                        {children}
                      </Paper>
                    )}
                    renderOption={option => (
                        <Tooltip
                        title={
                          option.disabled ? option.toolTip : (
                            option.displayName ? option.displayName : option.value
                          )
                        }>
                        <div
                            onClick={() => !option.disabled && handleChange(option)}
                            className={classes.listItem}>
                            <Radio
                            color="primary"
                            style={{ marginRight: 8 }}
                            checked={option === selectedOption}
                            />
                            <span className={classes.label}>
                            {option.displayName
                                ? option.displayName
                                : option.value}
                            </span>
                        </div>
                        </Tooltip>
                    )}
                    style={{ width: showStatusFilter ? 230 : 200 }}
                    classes={{
                      listbox: classes.autocompleteListbox
                    }}
                    renderInput={params => (
                        <TextField
                        {...params}
                        InputProps={{
                            ...params.InputProps,
                            className: classes.searchBar,
                            endAdornment: (
                              <Tooltip title="Clear Filter">
                                <IconButton
                                  onClick={handleClear}
                                  disabled={!selectedOption && (!filter.values || filter.values.length === 0) && (!inputValue || inputValue.length === 0)}
                                  className={classes.clearButton}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </Tooltip>)
                        }}
                        variant="outlined"
                        label={`Search ${title}`}
                        className={classes.searchInput}
                        />
                    )}
                    value={selectedOption ? selectedOption : null}
                />
                </div>
              </ClickAwayListener>
            </Grow>
          </Paper>
        )}
      </Popper>
    </>
  );
}